<template>
  <div v-if="showSSO" class="tw:flex tw:flex-col tw:gap-3" data-testid="sso-buttons">
    <LeadButton
      v-for="provider in providers"
      :key="provider"
      variant="white"
      block
      :icon="SSO_PROVIDERS[provider].logo"
      :loading="pendingProvider === provider"
      :disabled="pendingProvider !== null && pendingProvider !== provider"
      :data-testid="`sso-${provider}`"
      @click="signInWith(provider)">
      {{ $t(`sso.${flow}.${provider}`) }}
    </LeadButton>

    <div
      v-if="errorKey"
      class="tw:rounded-md tw:border tw:border-red-200 tw:bg-red-50 tw:p-3 tw:text-13 tw:text-red-600"
      role="alert"
      data-testid="sso-error">
      {{ $t(errorKey) }}
    </div>

    <div class="tw:flex tw:items-center tw:gap-3">
      <span class="tw:h-px tw:flex-1 tw:bg-border-secondary"></span>
      <span class="tw:text-12 tw:text-muted">{{ dividerLabel }}</span>
      <span class="tw:h-px tw:flex-1 tw:bg-border-secondary"></span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

import type { ClientError } from '@/api/client';
import { startSSO } from '@/api/sso';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import { rememberSSOFlow, SSO_PROVIDERS, ssoErrorKey } from '@/composables/useSSOProviders';
import type { SSOFlow, SSOProvider } from '@/types/sso';

interface Props {
  /** switches both providers on or off; the page derives it from the providers the deployment offers */
  showSSO: boolean;
  providers: SSOProvider[];
  flow: SSOFlow;
  /** the line between the buttons and the email form */
  dividerLabel: string;
  /** a LEAD path to continue at once signed in */
  returnPath?: string;
  /** an error to show from a previous attempt, e.g. after the provider redirected back with a failure */
  initialErrorCode?: string;
}

const props = defineProps<Props>();

/* ----------------------------------- STATE ---------------------------------- */
const pendingProvider = ref<SSOProvider | null>(null);
const errorKey = ref(props.initialErrorCode ? ssoErrorKey(props.initialErrorCode) : '');

/* --------------------------------- WATCHERS --------------------------------- */
watch(
  () => props.initialErrorCode,
  (code) => {
    if (code) errorKey.value = ssoErrorKey(code);
  }
);

/* --------------------------------- METHODS ---------------------------------- */
async function signInWith(provider: SSOProvider) {
  if (pendingProvider.value) return;

  pendingProvider.value = provider;
  errorKey.value = '';

  try {
    const url = await startSSO(provider, props.flow, props.returnPath);
    rememberSSOFlow(props.flow);
    // leaves the app; the provider redirects back to /sso/<provider>/callback
    window.location.assign(url);
  } catch (error) {
    errorKey.value = ssoErrorKey((error as ClientError)?.responseJson?.code);
    pendingProvider.value = null;
  }
}

/** back from the provider can restore this page from the bfcache, still waiting on the redirect it already made */
function resetAfterBackNavigation(event: PageTransitionEvent) {
  if (event.persisted) pendingProvider.value = null;
}

/* -------------------------------- LIFECYCLE --------------------------------- */
onMounted(() => window.addEventListener('pageshow', resetAfterBackNavigation));

onBeforeUnmount(() => window.removeEventListener('pageshow', resetAfterBackNavigation));
</script>
